/* eslint-disable @typescript-eslint/no-explicit-any */
export class ObjectUtils {
  /**
   * Method will return true if value is empty / null
   */
  static isNil<T>(object: T): boolean {
    return object == null;
  }

  /**
   * Method will check if the provided object
   * doesn't have any property
   */
  static isEmpty<T>(object: T): boolean {
    if (object === null || object === undefined || Object.entries(object).length === 0) {
      return true;
    }

    return Object.keys(object).length === 0;
  }

  /**
   * Method will check if the provided value is object
   */
  static isObject<T>(object: T): boolean {
    return object !== null && typeof object === 'object' && !Array.isArray(object);
  }

  /**
   * Method will check if the object have all
   * properties
   */
  static hasProperties<T extends object>(object: T, properties: string[]): boolean {
    const keys = Object.keys(object);

    for (const property of properties) {
      if (keys.indexOf(property) === -1) {
        return false;
      }
    }

    return true;
  }

  /**
   * Method will check if the object has:
   * - null
   * - undefined
   * - empty string
   */

  static hasEmptyProperties(object: object): boolean {
    return Object.values(object)?.filter(value => !value).length > 0;
  }

  /**
   * @description
   * Takes an Array<V>, and a grouping function,
   * and returns a Map of the array grouped by the grouping function.
   *
   * @param list An array of type V.
   * @param keyGetter A Function that takes the the Array type V as an input, and returns a value of type K.
   *                  K is generally intended to be a property key of V.
   *
   * @returns Map of the array grouped by the grouping function.
   */
  static groupBy<K, V>(list: Array<V>, keyGetter: (input: V) => K): Map<K, Array<V>> {
    const map = new Map<K, Array<V>>();
    list.forEach(item => {
      const key = keyGetter(item);
      const collection = map.get(key);
      if (!collection) {
        map.set(key, [item]);
      } else {
        collection.push(item);
      }
    });
    return map;
  }

  static getValueByDotSeparatedKey(key: string, data: object) {
    return key && data ? key.split('.').reduce((o: any, i: string) => (o ? o[`${i}`] : o), data) : key;
  }

  static getAllValuesByKey(current: any, targetKey: string, results: any[] = []): any[] {
    if (current === null || typeof current !== 'object') return results;
    if (Object.hasOwn(current, targetKey) && current[targetKey] !== null && current[targetKey] !== undefined) {
      results.push(current[targetKey]);
    }
    for (const key in current) {
      if (Object.hasOwn(current, key)) {
        this.getAllValuesByKey(current[key], targetKey, results);
      }
    }
    return results;
  }

  static getValueByKey(current: any, targetKey: string): any {
    if (current === null || typeof current !== 'object') return undefined;

    if (Object.hasOwn(current, targetKey)) {
      return current[targetKey];
    }

    const entries = Array.isArray(current) ? current : Object.values(current);

    for (const value of entries) {
      const result = this.getValueByKey(value, targetKey);
      if (result !== undefined) return result;
    }

    return undefined;
  }

  static convertDotKeysToObjects(obj: any) {
    const result: any = {};

    for (const key in obj) {
      if (Object.hasOwn(obj, key)) {
        const value = obj[`${key}`];

        const parts = key.split('.');
        const lastPart = parts.pop();

        if (parts.length > 0) {
          let currentObj = result;
          for (const part of parts) {
            currentObj[`${part}`] = currentObj[`${part}`] || {};
            currentObj = currentObj[`${part}`];
          }
          currentObj[`${lastPart}`] = value;
        } else {
          result[`${key}`] = value;
        }
      }
    }

    return result;
  }

  static convertObjectsToDotKeys(obj: any, prefix = '') {
    const result: any = {};

    for (const key in obj) {
      if (Object.hasOwn(obj, key)) {
        const value = obj[`${key}`];

        if (typeof value === 'object' && value !== null) {
          const nestedResult = ObjectUtils.convertObjectsToDotKeys(value, prefix + key + '.');
          Object.assign(result, nestedResult);
        } else {
          result[prefix + key] = value;
        }
      }
    }

    return result;
  }

  static keepProperties(obj: any, propertiesToKeep: string[]) {
    return Object.keys(obj)
      .filter(key => propertiesToKeep.includes(key))
      .reduce((prev: any, curr) => {
        prev[`${curr}`] = obj[`${curr}`];
        return prev;
      }, {});
  }
}
